import clsx from 'clsx'
import map from 'lodash/map'
import React, { FC, ReactNode, useMemo } from 'react'

import styles from './Milestones.module.scss'
import stylesV2 from './MilestonesV2.module.scss'

interface Milestone {
  buttonClassName?: string
  disabled?: boolean
  icon?: ReactNode
  id: string
  onClick?: () => void
  progress: number
  text?: string | ReactNode
}

export interface MilestonesProps {
  className?: string
  hideMilestoneIndicators?: boolean
  inverted?: boolean
  milestones: Milestone[]
  v2?: boolean
}
const MileStone: FC<{
  index: number
  milestone: Milestone
  numberOfMilestones: number
  v2?: boolean
}> = ({ index,
  milestone,
  numberOfMilestones,
  v2 }) => {
  const reached = useMemo(() => milestone.progress === 100, [milestone.progress])
  let firstPercentage = (100 / numberOfMilestones)
  if (numberOfMilestones > 2) {
    firstPercentage /= 1.5
  }
  let otherPercentage = (100 / numberOfMilestones)
  if (numberOfMilestones > 2) {
    otherPercentage += (firstPercentage / (numberOfMilestones - 1))
  }
  return (
    <div
      className={clsx(v2 ? stylesV2.milestone : styles.milestone, 'milestone')}
      style={{ width: index === 0 ? `${firstPercentage}%` : `${otherPercentage}%` }}
    >
      <div className={clsx(
        v2 ? stylesV2.milestoneLine : styles.milestoneLine,
        index === 0 && (v2 ? stylesV2.firstLine : styles.firstLine),
      )}
      >
        <div
          className={clsx(
            v2 ? stylesV2.milestoneIndicator : styles.milestoneIndicator,
            index === 0 && (v2 ? stylesV2.firstLine : styles.firstLine),
            reached && (v2 ? stylesV2.reached : styles.reached),
            milestone.progress > 0 && (v2 ? stylesV2.progressed : styles.progressed),
          )}
          style={{ width: `${milestone.progress}%` }}
        >
          {!v2 && !reached && (
            <div className={clsx(
              v2 ? stylesV2.milestoneIndicatorBubble : styles.milestoneIndicatorBubble,
              reached && (v2 ? stylesV2.reached : styles.reached),
            )}
            />
          )}
        </div>
      </div>
      <button
        className={clsx(
          v2 ? stylesV2.milestoneBubble : styles.milestoneBubble,
          milestone.buttonClassName,
          v2 && !milestone.disabled && milestone.onClick && stylesV2.clickable,
          reached && (v2 ? stylesV2.reached : styles.reached),
          index === 0 && (v2 ? stylesV2.first : styles.first),
          index === numberOfMilestones - 1 && (v2 ? stylesV2.last : styles.last),
        )}
        disabled={milestone.disabled || !milestone.onClick}
        onClick={() => {
          if (!milestone.disabled && milestone.onClick) {
            milestone.onClick()
          }
        }}
        type="button"
      >
        <span>{milestone.icon}</span>
        {milestone.text && (
          <p style={{
            paddingLeft:
              numberOfMilestones > 1 && index === numberOfMilestones - 1 ? `${100 / numberOfMilestones}%` : '0',
          }}
          >
            {milestone.text}
          </p>
        )}
      </button>
    </div>
  )
}

export const Milestones: FC<MilestonesProps> = ({ className, hideMilestoneIndicators, inverted, milestones, v2 }) => (
  <div className={clsx(
    className,
    v2 ? stylesV2.milestones : styles.milestones,
    'milestones',
    hideMilestoneIndicators && (v2 ? stylesV2.hideMilestoneIndicators : styles.hideMilestoneIndicators),
    inverted && (v2 ? stylesV2.inverted : styles.inverted),
  )}
  >
    {map(milestones, (milestone, index) => (
      <MileStone
        key={`milestone_${milestone.id}`}
        index={index}
        milestone={milestone}
        numberOfMilestones={milestones.length}
        v2={v2}
      />
    ))}
  </div>
)
